import { useMemo } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { glowBackground, resolveMotif } from "@/lib/appearance";

// Decorative-only ambient layer (glow + optional motif) for a given surface.
// Never receives pointer events or focus; carries no essential information.
// `overrideAtmosphere` / `overrideMode` let previews render without touching
// global state. `motifs` defaults to the org motif library from context.
export function AtmosphereLayer({
  surface = "page_edge",
  slot,                       // motif placement slot id (defaults to surface)
  overrideAtmosphere,
  overrideMode,
  overrideMotifs,
  overrideMobile,
  className = "",
}) {
  const ctx = useTheme() || {};
  const atmosphere = overrideAtmosphere ?? ctx.atmosphere;
  const mode = overrideMode ?? ctx.resolved ?? "light";
  const motifs = overrideMotifs ?? ctx.motifs ?? [];
  const personalOff = overrideAtmosphere ? false : ctx.atmospherePersonal === "off";
  const isMobile = overrideMobile ?? (typeof window !== "undefined" && window.innerWidth < 640);
  const slotId = slot || surface;

  const bg = useMemo(
    () => (personalOff ? "" : glowBackground(atmosphere, { surface, mode })),
    [atmosphere, surface, mode, personalOff]
  );
  const motif = useMemo(
    () => (personalOff ? null : resolveMotif(atmosphere, motifs, slotId, { mode, isMobile })),
    [atmosphere, motifs, slotId, mode, isMobile, personalOff]
  );

  if (!bg && !motif) return null;

  return (
    <div className={`atmosphere-layer ${className}`} aria-hidden="true" data-testid={`atmosphere-${surface}`}>
      {bg && <div className="atmosphere-glow" style={{ backgroundImage: bg }} />}
      {motif && (
        <img
          src={motif.url}
          alt=""
          aria-hidden="true"
          draggable="false"
          className="atmosphere-motif"
          style={{
            ...motif.style,
            width: `${180 * (motif.slot.scale || 1)}px`,
            maxWidth: "40%",
            opacity: motif.slot.opacity ?? 0.06,
            transform: `${motif.style.transform || ""} rotate(${motif.slot.rotation || 0}deg)`.trim(),
            filter: motif.slot.tint === "light" ? "brightness(0) invert(1)"
              : motif.slot.tint === "dark" ? "brightness(0)" : "none",
          }}
        />
      )}
    </div>
  );
}

export default AtmosphereLayer;
